import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  colorBackgroundBody,
  colorBackground,
  colorFieldsBorder,
  colorText,
  colorLightGray,
} from "../../utils/palette";
import { borderRadius } from "../../utils/styles";
import KPIBar from "./kpis/KPIBar";
import { DEFAULT_KPI_IDS } from "./kpis/kpiRegistry";
import { DEFAULT_GRID_IDS } from "./customizer/gridMetricRegistry";
import CustomizerPanel from "./customizer/CustomizerPanel";
import InsightsPanel from "./insights/InsightsPanel";
import InsightsIntroPopup from "./insights/InsightsIntroPopup";
import MetricsGrid from "./charts/MetricsGrid";
import FilterBar from "./filters/FilterBar";
import { ColumnHeading } from "../../shared/components/TableElements";
import { getInitialDates } from "./filters/dateUtils";
import { putApi } from "../../api/getUser";

const Page = styled.div`
  padding: 0 ${({ $insightsOpen }) => ($insightsOpen ? "408px" : "28px")} 24px
    28px;
  min-height: 100%;
  background: #eef1f5;
  transition: padding-right 0.22s ease-out;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const PageTitle = styled.h1`
  font-size: 18px;
  font-weight: 500;
  //color: ${colorText};
  margin: 0;
`;

const AIInsightsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;

  ${({ $active }) =>
    $active
      ? `
    background: #5b4b9e;
    border: 1px solid #5b4b9e;
    color: #fff;
    &:hover { filter: brightness(1.08); }
  `
      : `
    background: linear-gradient(100deg, #eef1fa, #f3f0fa);
    border: 1px solid #D9DCEC;
    color: #5b4b9e;
    &:hover { filter: brightness(0.985); }
  `}
`;

const AIInsightsBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.18);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
`;

const CustomizeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${colorBackground};
  border: 1px solid ${colorFieldsBorder};
  border-radius: ${borderRadius};
  color: ${colorText};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.15s;

  &:hover {
    //border-color: ${colorText};
    background: ${colorLightGray};
  }
`;

export default function DashboardV2() {
  const { t } = useTranslation("common");
  const [activeKpiIds, setActiveKpiIds] = useState(DEFAULT_KPI_IDS);
  const [activeGridIds, setActiveGridIds] = useState(DEFAULT_GRID_IDS);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [customizerOpen, setCustomizerOpen] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [insightCount, setInsightCount] = useState(0);
  const [filters, setFilters] = useState(() => ({
    ...getInitialDates(),
    vehicle_ids: [],
    group_ids: [],
  }));

  useEffect(() => {
    async function loadPreferences() {
      try {
        const res = await putApi(
          { querystring: "get_dashboard_preferences" },
          "dashboard",
        );
        if (res?.kpi_ids)
        setActiveKpiIds(res.kpi_ids);
        if (res?.grid_ids)
        setActiveGridIds(res.grid_ids);
      } catch {
        // network error — keep defaults silently
      } finally {
        setPrefsLoaded(true);
      }
    }
    loadPreferences();
  }, []);

  async function handleCustomizerSave(kpiIds, gridIds) {
    setActiveKpiIds(kpiIds);
    setActiveGridIds(gridIds);
    setCustomizerOpen(false);
    await putApi(
      {
        querystring: "save_dashboard_preferences",
        kpi_ids: kpiIds,
        grid_ids: gridIds,
      },
      "dashboard",
    );
  }

  return (
    <Page $insightsOpen={insightsOpen}>
      <PageHeader>
        <ColumnHeading>{t("dashboard")}</ColumnHeading>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <AIInsightsButton
            $active={insightsOpen}
            onClick={() => setInsightsOpen((v) => !v)}
          >
            <i
              className="fi fi-rr-sparkles"
              style={{ fontSize: 14, lineHeight: 1 }}
            />
            {t("ai_insights")}
            {/* {insightCount > 0 && (
              <AIInsightsBadge>{insightCount}</AIInsightsBadge>
            )} */}
          </AIInsightsButton>
          <CustomizeButton onClick={() => setCustomizerOpen(true)}>
            ⚙ {t("customize_dashboard")}
          </CustomizeButton>
        </div>
      </PageHeader>

      <FilterBar onChange={setFilters} />

      {prefsLoaded && (
        <>
          <KPIBar activeIds={activeKpiIds} filters={filters} />
          <MetricsGrid activeGridIds={activeGridIds} filters={filters} />
        </>
      )}

      <CustomizerPanel
        isOpen={customizerOpen}
        onClose={() => setCustomizerOpen(false)}
        activeKpiIds={activeKpiIds}
        activeGridIds={activeGridIds}
        onSave={handleCustomizerSave}
      />

      <InsightsPanel
        isOpen={insightsOpen}
        onClose={() => setInsightsOpen(false)}
        onCountChange={setInsightCount}
      />

      <InsightsIntroPopup onOpenInsightsPanel={() => setInsightsOpen(true)} />
    </Page>
  );
}
